import { Box } from '@material-ui/core';
import {
  getLoanById,
  getLoanStatusesByLoanId,
  getOrderStatusesByLoanId,
  Loan,
  LoanDesistReason,
  LoanStatus,
  patchChangeLoanStatus,
} from '@nemuru-eng/js-api-client';

import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import {
  LoanStatusChangeSchema,
  LoanStatusChangeType,
} from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/schemas/LoanStatusChangeSchema';
import { LoanDesistReasonOptions } from '@/components/widgets/LoanDetailActions/constants/LoanDesistReasonOptions';
import { LoanDetailAction } from '@/components/widgets/LoanDetailActions/types/LoanDetailAction';
import { ApplicationTypeMap } from '@/config/maps/ApplicationType.map';
import { ApplicationType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

export const LoanStatusChangeMessageConfirmation = ({
  loan,
  currentAction,
  opened,
  setOpened,
  handleClose,
}: {
  loan: Loan;
  currentAction: LoanDetailAction;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}) => {
  const { request, loading } = useRequest();
  const { revalidate: revalidateGetLoanStatuses } = useCache(getLoanStatusesByLoanId, { loanId: loan.id });
  const { revalidate: revalidateGetOrderStatuses } = useCache(getOrderStatusesByLoanId, { loanId: loan.id });
  const { revalidate: revalidateGetLoan } = useCache(getLoanById, { loanId: loan.id });
  const { getTextIdTranslation } = useTranslation();
  const getActionParamsByActionName = (actionName: LoanDetailAction) => {
    switch (actionName) {
      case LoanDetailAction.CANCEL:
        return {
          newStatus: LoanStatus.STATUS_CANCELLED,
          dialogTitleTextId: 'CANCEL_LOAN',
          dialogDescriptionTextId: 'DO_YOU_WISH_TO_SET_THIS_LOAN_STATUS_AS_STATUS_CANCELLED',
          actionButtonTextId: 'CANCEL_LOAN_BTN',
        };
      case LoanDetailAction.CANCEL_TEST:
        return {
          newStatus: LoanStatus.STATUS_CANCELLED_TEST,
          dialogTitleTextId: 'CANCEL_TEST',
          dialogDescriptionTextId: 'DO_YOU_WISH_TO_SET_THIS_LOAN_STATUS_AS_STATUS_CANCELLED_TEST',
          actionButtonTextId: 'CANCEL_TEST_BTN',
        };
      case LoanDetailAction.DESIST:
        return {
          newStatus: LoanStatus.STATUS_DESISTED,
          dialogTitleTextId: 'DESIST_LOAN',
          dialogDescriptionTextId: 'PLEASE_SELECT_THE_REASON_WHY_YOU_ARE_DESISTING_THIS_LOAN',
          actionButtonTextId: 'DESIST_LOAN_BTN',
        };
      case LoanDetailAction.FORMALIZE_APPLICATION:
        return {
          newStatus: LoanStatus.STATUS_DOCS_APPROVED,
          dialogTitleTextId: 'FORMALIZE_APPLICATION',
          dialogDescriptionTextId: 'DO_YOU_WISH_TO_FORMALIZED_THIS_APPLICATION',
          actionButtonTextId: 'FORMALIZE_APPLICATION_BTN',
        };
      case LoanDetailAction.MARK_LOAN_AS_PAID:
        return {
          newStatus: LoanStatus.STATUS_ACTIVE_TRIAL,
          dialogTitleTextId: 'MARK_LOAN_AS_PAID',
          dialogDescriptionTextId: 'DO_YOU_WISH_TO_MARK_THIS_LOAN_AS_PAID',
          actionButtonTextId: 'MARK_LOAN_AS_PAID_BTN',
        };
    }
  };

  const actionParams = getActionParamsByActionName(currentAction);

  const changeLoanStatusByActionType = async (useFormValues: LoanStatusChangeType) => {
    if (!actionParams) return;
    const applicationType = ApplicationTypeMap.fromAssetType(loan?.assetReference[0].type);

    const patchChangeLoanStatusPayload: PatchChangeLoanStatusRequestParams = {
      id: loan.id,
      status: actionParams.newStatus,
    };
    if (currentAction === LoanDetailAction.DESIST) {
      patchChangeLoanStatusPayload.additional = useFormValues.additional as LoanDesistReason;
    }
    await request(patchChangeLoanStatus, patchChangeLoanStatusPayload);
    revalidateGetLoan();
    revalidateGetLoanStatuses();
    applicationType === ApplicationType.ORDER && revalidateGetOrderStatuses();
    handleClose();
  };

  if (!actionParams) return <></>;

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      onSubmitForm={changeLoanStatusByActionType}
      saveButtonTextId={actionParams.actionButtonTextId}
      title={actionParams.dialogTitleTextId}
      handleClose={handleClose}
      isLoading={loading}
      formSchema={() => LoanStatusChangeSchema(getTextIdTranslation, currentAction)}
      forceEnableSaveButton
    >
      <>
        <Box pt={1}>
          <Font variant={'body2'} color={'textSecondary'}>
            <Text tId={actionParams.dialogDescriptionTextId} />
          </Font>
        </Box>
        {currentAction === LoanDetailAction.DESIST && (
          <Box pt={3}>
            <SelectFormField name={'additional'} label={'DESIST_REASON'} selectOptions={LoanDesistReasonOptions} />
          </Box>
        )}
      </>
    </EditDialogWrapper>
  );
};
export interface PatchChangeLoanStatusRequestParams {
  id: string;
  status: LoanStatus;
  additional?: LoanDesistReason;
}
